import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Bell, Clock, BookOpen, Crown, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { upsertPushPrefs, deletePushSubscription } from "../lib/supabase";

const VAPID_PUBLIC_KEY =
  process.env.REACT_APP_VAPID_PUBLIC_KEY ||
  "BOOHfS3SrDwHcXAcRSW4nz_USuHY4wZkttlOg1xHDhDz95Vd_nqh44wxOIF3-1GeGXwUBLm2EqTeweLJMkunQfU";

const urlBase64ToUint8Array = (base64String) => {
  if (!base64String) return new Uint8Array();
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  const notifSupported = typeof window !== 'undefined' && 'Notification' in window;
  const isBrave = typeof window !== 'undefined' && navigator.brave !== undefined;

  const [permission, setPermission] = useState(() =>
    notifSupported ? Notification.permission : 'denied'
  );

  const [dailyReading, setDailyReading] = useState(() =>
    localStorage.getItem("notif-daily-reading") === "true"
  );

  const [dailyReadingTime, setDailyReadingTime] = useState(() =>
    localStorage.getItem("notif-daily-reading-time") || "07:00"
  );

  const [reminderEnabled, setReminderEnabled] = useState(() =>
    localStorage.getItem("notif-reminder") === "true"
  );

  const [reminderTime, setReminderTime] = useState(() =>
    localStorage.getItem("notif-reminder-time") || "08:00"
  );

  // Convert local HH:MM to UTC HH:MM
  const localTimeToUTC = (hhmm) => {
    const [h, m] = (hhmm || "07:00").split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  };

  // Play chime sound
  const playChimeSound = () => {
    try {
      const audio = new Audio('/sounds/chime.wav');
      audio.volume = 0.8;
      audio.play().catch(() => {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(659.25, ctx.currentTime);
        osc.frequency.setValueAtTime(880.00, ctx.currentTime + 0.12);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.7);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.7);
      });
    } catch (e) {
      console.warn("Chime play error:", e);
    }
  };

  // Get or create push subscription
  const getSubscription = async () => {
    if (!('serviceWorker' in navigator)) {
      toast.error("Votre navigateur ne supporte pas les notifications push.");
      return null;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      if (!('pushManager' in reg)) {
        toast.error("Push Manager non disponible dans ce navigateur.");
        return null;
      }
      const existing = await reg.pushManager.getSubscription();
      if (existing) await existing.unsubscribe();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      return sub;
    } catch (e) {
      console.error("Push subscription error:", e);
      toast.error("Erreur abonnement push : " + (e?.message || String(e)));
      return null;
    }
  };

  // Save all push preferences to DB
  const savePreferences = async (daily, dailyTime, reminder, reminderT) => {
    try {
      const sub = await getSubscription();
      if (!sub) {
        toast.error("Impossible de créer l'abonnement push. Vérifiez les permissions.");
        return;
      }
      if (!daily && !reminder) {
        await deletePushSubscription(sub.endpoint);
      } else {
        const ok = await upsertPushPrefs(sub, {
          daily_reading: daily,
          daily_utc: daily ? localTimeToUTC(dailyTime) : null,
          reminder_enabled: reminder,
          reminder_utc: reminder ? localTimeToUTC(reminderT) : null,
        }, user?.id);
        if (!ok) toast.error("Erreur sauvegarde. Rechargez la page et réessayez.");
      }
    } catch (e) {
      console.error("Save preferences error:", e);
      toast.error("Erreur préférences : " + (e?.message || String(e)));
    }
  };

  // Request notification permission
  const handleRequestPermission = async () => {
    if (!notifSupported) {
      toast.error("Votre navigateur ne supporte pas les notifications.");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm === 'granted') {
        playChimeSound();
        toast.success("Notifications autorisées avec succès !");
        if ('serviceWorker' in navigator) {
          const reg = await navigator.serviceWorker.ready;
          if (reg?.showNotification) {
            reg.showNotification("📖 Bible Fillion 1920", {
              body: "Les notifications sont activées sur votre appareil !",
              icon: `${window.location.origin}/icons/icon-192.png`,
              badge: `${window.location.origin}/icons/icon-72.png`,
            });
          }
        }
      } else {
        toast.error("Autorisation non accordée.");
      }
    } catch (e) {
      toast.error("Erreur d'activation : " + (e?.message || ""));
    }
  };

  // Toggle daily reading
  const handleDailyToggle = async (checked) => {
    setDailyReading(checked);
    localStorage.setItem("notif-daily-reading", checked.toString());
    await savePreferences(checked, dailyReadingTime, reminderEnabled, reminderTime);
    toast.success(checked ? `Lecture du matin activée à ${dailyReadingTime}` : "Lecture du matin désactivée");
  };

  // Change daily reading time
  const handleDailyTimeChange = async (newTime) => {
    setDailyReadingTime(newTime);
    localStorage.setItem("notif-daily-reading-time", newTime);
    if (dailyReading) {
      await savePreferences(true, newTime, reminderEnabled, reminderTime);
      toast.success(`Lecture du matin : ${newTime}`);
    }
  };

  // Toggle reminder (Premium)
  const handleReminderToggle = async (checked) => {
    if (!isPremium && checked) { navigate("/premium"); return; }
    setReminderEnabled(checked);
    localStorage.setItem("notif-reminder", checked.toString());
    await savePreferences(dailyReading, dailyReadingTime, checked, reminderTime);
    toast.success(checked ? `Rappel programmé pour ${reminderTime}` : "Rappel désactivé");
  };

  // Change reminder time
  const handleReminderTimeChange = async (newTime) => {
    setReminderTime(newTime);
    localStorage.setItem("notif-reminder-time", newTime);
    if (reminderEnabled) {
      await savePreferences(dailyReading, dailyReadingTime, true, newTime);
      toast.success(`Heure mise à jour : ${newTime}`);
    }
  };

  // Test notification
  const handleTestNotification = async () => {
    playChimeSound();
    if (notifSupported && permission !== 'granted') {
      const perm = await Notification.requestPermission().catch(() => 'denied');
      setPermission(perm);
    }
    try {
      if ('serviceWorker' in navigator) {
        let reg = await navigator.serviceWorker.getRegistration();
        if (!reg) reg = await navigator.serviceWorker.register('/sw.js');
        if (reg?.showNotification) {
          await reg.showNotification("🔔 Bible Fillion — Test", {
            body: "« Au commencement Dieu créa le ciel et la terre. » — Genèse 1:1",
            icon: `${window.location.origin}/icons/icon-192.png`,
            badge: `${window.location.origin}/icons/icon-72.png`,
            tag: "bible-fillion-test-" + Date.now(),
            data: { url: "/" }
          });
        }
      }
      toast.success("🔔 Carillon joué & notification envoyée !", {
        description: "Vérifiez dans la barre en haut de votre écran."
      });
    } catch (e) {
      toast.success("🔔 Carillon sonore joué avec succès !");
    }
  };

  // Hard reset cache
  const handlePurgeCache = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      window.location.reload(true);
    } catch { window.location.reload(true); }
  };

  return (
    <div className="container mx-auto max-w-2xl px-4 py-8 animate-fade-in font-body">
      <div className="text-center mb-8">
        <h1 className="font-heading text-3xl text-ink-primary mb-2">Notifications & Rappels</h1>
        <p className="text-ink-secondary text-sm">
          Recevez les lectures et gardez votre rythme spirituel quotidien.
        </p>
      </div>

      {/* Brave browser warning */}
      {isBrave && (
        <div className="mb-6 rounded-lg border border-orange-300 bg-orange-50 p-4 text-sm text-orange-800">
          <p className="font-semibold mb-1">⚠️ Brave bloque les notifications push</p>
          <p>Brave Shield bloque le service Web Push (FCM) par défaut. Pour recevoir les rappels, utilisez <strong>Chrome</strong>, <strong>Edge</strong> ou <strong>Firefox</strong>.</p>
        </div>
      )}

      {/* 1. Statut */}
      <Card className="mb-6 border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Bell className="h-5 w-5 text-rubric-red" />
            Statut de l'appareil
          </CardTitle>
        </CardHeader>
        <CardContent>
          {permission === 'granted' ? (
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-900 text-sm">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>Notifications et alertes autorisées sur cet appareil.</span>
            </div>
          ) : permission === 'denied' ? (
            <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg border border-amber-200 dark:border-amber-900 text-sm space-y-2 text-amber-900 dark:text-amber-200">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
                <span>Notifications actuellement bloquées par votre navigateur</span>
              </div>
              <ol className="list-decimal list-inside text-xs space-y-1 text-ink-primary">
                <li>Touchez l'icône <strong>🔒 ou ⚙️</strong> à gauche de l'adresse du site.</li>
                <li>Touchez <strong>Autorisations</strong> &gt; <strong>Notifications</strong>.</li>
                <li>Activez <strong>Autoriser</strong> puis actualisez la page.</li>
              </ol>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-secondary">Les notifications ne sont pas encore activées.</span>
              <Button onClick={handleRequestPermission} className="bg-rubric-red hover:bg-rubric-red/90 text-white text-sm">
                <Bell className="h-4 w-4 mr-1.5" /> Autoriser
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 2. Lecture du matin */}
      <Card className="mb-6 border-border shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-rubric-red" />
              <CardTitle className="text-lg font-heading">Lecture du matin</CardTitle>
            </div>
            <Switch checked={dailyReading} onCheckedChange={handleDailyToggle} />
          </div>
          <CardDescription className="text-sm text-ink-secondary">
            Recevez chaque jour le passage de la Sainte Bible du jour.
          </CardDescription>
        </CardHeader>
        {dailyReading && (
          <CardContent>
            <div className="flex items-center gap-4 pt-1">
              <Label className="text-sm text-ink-primary font-medium">Heure :</Label>
              <Input
                type="time"
                value={dailyReadingTime}
                onChange={(e) => handleDailyTimeChange(e.target.value)}
                className="w-32 text-center font-mono"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* 3. Rappel quotidien personnalisé (Premium) */}
      <Card className="mb-6 border-border shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-ink-secondary" />
              <CardTitle className="text-lg font-heading">Rappel de lecture</CardTitle>
              {!isPremium && (
                <Badge variant="outline" className="border-gold-leaf text-gold-leaf text-xs">
                  <Crown className="h-3 w-3 mr-1" /> Premium
                </Badge>
              )}
            </div>
            {isPremium && (
              <Switch checked={reminderEnabled} onCheckedChange={handleReminderToggle} />
            )}
          </div>
          <CardDescription className="text-sm text-ink-secondary">
            Programmez une sonnerie et un rappel à l'heure exacte de votre choix.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isPremium ? (
            <div className="text-center py-3 bg-parchment-light dark:bg-card/50 rounded-lg border border-border">
              <p className="text-xs text-ink-secondary mb-3">
                L'horaire de rappel sur-mesure est réservé aux membres Premium.
              </p>
              <Button onClick={() => navigate("/premium")} size="sm" className="bg-gold-leaf hover:bg-gold-leaf/90 text-white">
                <Crown className="h-3.5 w-3.5 mr-1" /> Débloquer Premium
              </Button>
            </div>
          ) : (
            reminderEnabled && (
              <div className="flex items-center gap-4 pt-1">
                <Label className="text-sm text-ink-primary font-medium">Heure du rappel :</Label>
                <Input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => handleReminderTimeChange(e.target.value)}
                  className="w-32 text-center font-mono"
                />
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* 4. Test & Reset */}
      <div className="text-center pt-4 pb-8 space-y-4">
        <Button
          onClick={handleTestNotification}
          className="bg-rubric-red hover:bg-rubric-red/90 text-white font-body px-8 py-3 text-base shadow-md transition-all hover:scale-105"
        >
          🔔 Tester la notification & la sonnerie
        </Button>
        <div>
          <button
            onClick={handlePurgeCache}
            className="text-xs text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1.5 underline"
          >
            <RefreshCw className="h-3 w-3" />
            Réinitialiser le cache & recharger la dernière version
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;